// 使用者驗證 / token 生命週期服務：寫入 token cookie、以 refresh token 換發，
// 以及 401 / GO_LOGIN 時的重新登入流程（403 為無權限，重新登入無法解決，不在此處理）。
// 刷新採事件驅動：由請求 interceptor 在收到 401 時觸發（見 apis/core/interceptors.js），不使用計時器主動排程。
import { getCookie, setCookie } from '../utils/cookie';
import { useToast } from '../composables/useToast';
import authAPI from '../apis/auth';

// 將 token 存入 cookie
export function saveTokenToCookie(expiration, token, tokenType, isLink) {
  const date = new Date();
  const expirationTimeStamp = expiration ? expiration * 1000 : date.getTime() + (20 * 24 * 60 * 60 * 1000);
  date.setTime(expirationTimeStamp);

  // token 類型與綁定狀態需比 access token 長壽：access token 過期被瀏覽器刪除後，下次載入仍要靠這
  // 兩個值判斷 refresh 換回來的 token 類型與操作權限，故給較長效期（20 天，約當 refresh token 生命週期）。
  const metaDate = new Date();
  metaDate.setTime(metaDate.getTime() + (20 * 24 * 60 * 60 * 1000));

  // Authorization 改由各 Request 實例的 request 攔截器讀 cookie 帶入，這裡只負責寫 cookie
  setCookie('rpg_temp_token', token, { expires: date });
  setCookie('rpg_temp_token_type', tokenType, { expires: metaDate });
  setCookie('rpg_temp_token_is_link', isLink, { expires: metaDate });
}

// 同一頁上問答區（本專案）、評價系統（rpg-teachable-review）、快速連結（teachable-editor lecture_header）共用的換新鎖。
// 三邊是各自獨立的 bundle，模組內的變數互相看不到，所以把進行中的換新 Promise 掛在 window 上共用：
// - 值：Promise<string|null>，換到 resolve 新的 access token，換不到 resolve null，不會 reject
// - 由發起換新的那一方把新 token 寫進 rpg_temp_token cookie，其他人等同一個結果
// 三邊必須維持同一個約定，改這裡要一起改另外兩邊。
const SHARED_REFRESH_KEY = '__hexRpgTokenRefresh';

// 以 httpOnly cookie 中的 refresh token 換取新的短效 access token（1 小時），回傳新 token，換不到回傳 null。
// refresh_token cookie 與 XSRF-Token 由 authRequest 實例自動帶上（withCredentials + xsrf 設定）。
// 每次成功呼叫後端都會輪替 refresh token cookie；若送出已被輪替的舊 token，後端會觸發 reuse detection 並撤銷整個 session，
// 所以同一頁有人在換就等同一個結果（見 SHARED_REFRESH_KEY），不自己再發一次。
export const refreshToken = () => {
  if (!window[SHARED_REFRESH_KEY]) {
    window[SHARED_REFRESH_KEY] = authAPI.apiPostUserTokenRefresh()
      .then((res) => {
        // refresh 端點的 401／錯誤由 interceptor 以 _isRefreshCall 略過並回傳 undefined，由外層 401 處理決定是否 reLogin
        if (!res || !res.data.access_token) return null;
        // refresh 僅回傳新的 access_token 與 expiration，token 類型與綁定狀態沿用目前 cookie 的值
        const tokenType = getCookie('rpg_temp_token_type') || 'teachable';
        const isLink = getCookie('rpg_temp_token_is_link') || 'false';
        saveTokenToCookie(res.data.expiration, res.data.access_token, tokenType, isLink);
        return res.data.access_token;
      })
      .catch(() => null)
      .finally(() => {
        window[SHARED_REFRESH_KEY] = null;
      });
  }
  return window[SHARED_REFRESH_KEY];
};

export const directToRPGUserSystem = (bind) => {
  window.location.href = `${process.env.VUE_APP_USER_SYSTEM_DOMAIN}/#/login?bind=${bind}&redirectType=teachable&route=${window.location.pathname}/`;
};

// 併發的 401 / GO_LOGIN 只觸發一次重新登入，避免同時打多支登入 API
let reLoginInFlight = false;

// 當 api 有 401 錯誤或是 errorCode 為 GO_LOGIN 時，重新登入
export const reLogin = () => {
  if (reLoginInFlight) return;
  const { postMessage } = useToast();
  const userInfo = document.querySelector('#fedora-data');
  if (!(userInfo && userInfo.dataset.id)) return;
  reLoginInFlight = true;
  authAPI.apiPostLogin({
    teachable_id: userInfo.dataset.id || '',
    email: userInfo.dataset.email || '',
  }).then((res) => {
    const tokenExp = res.data.expiration;
    if (!res.data.is_link) { // 未綁定過，直接使用 teachable token
      saveTokenToCookie(tokenExp, res.data.access_token, 'teachable', 'false');
      window.location.reload();
    } else if (['production', 'development'].includes(process.env.VUE_APP_ENV)) {
      // 已綁定過
      directToRPGUserSystem(true);
    } else {
      saveTokenToCookie(tokenExp, res.data.access_token, 'teachable', 'true');
      reLoginInFlight = false;
    }
  }).catch((error) => {
    reLoginInFlight = false;
    console.log(error);
    postMessage('danger', '取得使用者發生錯誤', `${error?.response?.status} - ${error?.response?.data.message}`);
  });
};
